import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  ...props
}) => {
  // Glassmorphic button styles
  const baseClasses = 'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 shadow-md backdrop-blur-md';
  
  const variantClasses = {
    primary: 'bg-gradient-to-r from-primary-500/80 to-primary-600/80 hover:from-primary-500/90 hover:to-primary-600/90 text-white focus:ring-primary-500 border border-primary-400/30 hover:shadow-lg hover:shadow-primary-500/30 transform hover:-translate-y-0.5',
    secondary: 'bg-gradient-to-r from-secondary-500/80 to-secondary-600/80 hover:from-secondary-500/90 hover:to-secondary-600/90 text-white focus:ring-secondary-500 border border-secondary-400/30 hover:shadow-lg hover:shadow-secondary-500/30 transform hover:-translate-y-0.5',
    outline: 'text-gray-200 border border-gray-600/40 focus:ring-gray-500 hover:border-gray-400/50 backdrop-blur-lg' + ' ' + 'bg-gray-800/30 hover:bg-gray-700/40',
    danger: 'bg-gradient-to-r from-red-500/80 to-red-600/80 hover:from-red-500/90 hover:to-red-600/90 text-white focus:ring-red-500 border border-red-400/30 hover:shadow-lg hover:shadow-red-500/30 transform hover:-translate-y-0.5',
    success: 'bg-gradient-to-r from-green-500/80 to-green-600/80 hover:from-green-500/90 hover:to-green-600/90 text-white focus:ring-green-500 border border-green-400/30 hover:shadow-lg hover:shadow-green-500/30 transform hover:-translate-y-0.5',
  };
  
  const sizeClasses = {
    sm: 'text-xs px-3 py-1.5',
    md: 'text-sm px-4 py-2',
    lg: 'text-base px-6 py-3',
  };
  
  const widthClass = fullWidth ? 'w-full' : '';
  
  const disabledClass = disabled || isLoading 
    ? 'opacity-60 cursor-not-allowed hover:bg-opacity-100' 
    : '';

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${disabledClass} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      
      {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
    </button>
  );
};
